import { format, isAfter, isBefore, addDays, differenceInDays, startOfDay, endOfDay } from 'date-fns';

// Shared helper function to extract API names
export const extractApiName = (description: string): string | null => {
  if (!description || typeof description !== 'string') return null;
  
  // Match both API and system patterns - order matters for specificity
  const patterns = [
    /\b(RPA[-][a-z0-9-]+[-](?:sys|prc|exp))\b/i,  // matches RPA-*-sys first
    /\b([a-z0-9-]+[-]etl[-][a-z0-9-]+)\b/i,       // matches *-etl-*
    /\b([a-z0-9-]+[-](?:sys|prc|exp))\b/i,        // matches *-sys, *-prc, *-exp
    /\b(qad[-][a-z0-9-]+)\b/i,                    // matches qad-*
    /\b(erp[-][a-z0-9-]+)\b/i,                    // matches erp-*
    /\b([a-z0-9-]+[-]?api)\b/i                     // matches *-api last
  ];
  
  for (const pattern of patterns) {
    const match = description.match(pattern);
    if (match) return match[0].toLowerCase(); // Normalize to lowercase
  }
  
  return null;
};

// Shared helper function to check deployment-related descriptions
export const isDeploymentRelated = (description: string): boolean => {
  if (!description || typeof description !== 'string') return false;
  
  const deploymentKeywords = [
    'deploy',
    'deployment',
    'redeploy',
    'deployement', // common misspelling
    'release',     // additional keywords
    'rollout',
    'promote',
    'upgrade',
    'install',
    'migration',
    'configure',
    'update'
  ];
  
  const systemKeywords = [
    '-sys',
    '-prc',
    '-exp',
    'etl-',
    'rpa-',
    'qad-',
    'erp-'
  ];
  
  const lowerDesc = description.toLowerCase();
  return deploymentKeywords.some(keyword => lowerDesc.includes(keyword)) ||
         systemKeywords.some(keyword => lowerDesc.includes(keyword.toLowerCase()));
};

interface DateRange {
  from: Date;
  to?: Date;
}

// Main function to process deployments and incidents
export const processDeploymentData = (
  catalogTasks: any[], 
  incidents: any[],
  dateRange?: DateRange
) => {
  const deploymentTimelineData = new Map();
  const seenTaskNumbers = new Set<string>();
  const seenIncidentNumbers = new Set<string>();
  
  // Ensure we have valid date range
  const start = dateRange ? startOfDay(dateRange.from) : startOfDay(addDays(new Date(), -30));
  const end = dateRange?.to ? endOfDay(dateRange.to) : endOfDay(new Date());
  
  // First pass: Process all valid deployment tasks
  catalogTasks.forEach(task => {
    const taskNumber = task.number || task.taskNumber;
    const description = task['Short description'] || task.shortDescription || task.description || '';
    let apiName = task.api_name || extractApiName(description);
    
    // Special case: if description contains system keywords but no API name was found
    if (!apiName && description.toLowerCase().includes('sys')) {
      const words = description.split(/\s+/);
      for (const word of words) {
        if (word.toLowerCase().includes('sys')) {
          apiName = word.trim();
          break;
        }
      }
    }
    
    apiName = apiName || 'Unknown API';
    const taskDate = new Date(task.opened || task.created || task.date);
    
    // Skip if outside date range - use exact date comparison
    if (taskDate < start || taskDate > end) {
      return;
    }
    
    // Validate deployment task - relaxed validation to catch more deployments
    if (taskNumber && 
        typeof taskNumber === 'string' && 
        (isDeploymentRelated(description) || description.toLowerCase().includes('sys')) && 
        !seenTaskNumbers.has(taskNumber.trim().toUpperCase())) {
      
      seenTaskNumbers.add(taskNumber.trim().toUpperCase());
      const dateKey = format(taskDate, 'MMM d, yyyy');
      
      if (!deploymentTimelineData.has(apiName)) {
        deploymentTimelineData.set(apiName, new Map());
      }
      
      const apiData = deploymentTimelineData.get(apiName);
      if (!apiData.has(dateKey)) {
        apiData.set(dateKey, {
          date: dateKey,
          deployments: 0,
          deploymentDetails: [],
          incidentDetails: [],
          postDeploymentIncidents: 0
        });
      }
      
      const dayData = apiData.get(dateKey);
      dayData.deployments = 1;
      dayData.deploymentDetails.push({
        taskNumber,
        date: taskDate,
        description,
        apiName
      });
    }
  });
  
  // Process incidents
  incidents.forEach(incident => {
    const incidentNumber = incident.number || incident.incident_number;
    const incidentDate = new Date(incident.opened || incident.created || incident.incident_created_date);
    const incidentDesc = incident['Short description'] || incident.shortDescription || incident.description || '';
    const incidentApiName = incident.api_name || extractApiName(incidentDesc);
    
    // Skip if outside date range
    if (incidentDate < start || incidentDate > end) {
      return;
    }
    
    if (!incidentNumber || 
        seenIncidentNumbers.has(incidentNumber.trim().toUpperCase()) ||
        !incidentApiName ||
        !deploymentTimelineData.has(incidentApiName)) {
      return;
    }
    
    seenIncidentNumbers.add(incidentNumber.trim().toUpperCase());
    
    // Find the most recent deployment within 7 days before the incident
    const apiData = deploymentTimelineData.get(incidentApiName);
    let linkedDeploymentDate = null;
    let shortestDaysDiff = Infinity;
    
    apiData.forEach((dayData, dateKey) => {
      if (dayData.deploymentDetails.length === 0) return;
      
      const deploymentDate = new Date(dayData.deploymentDetails[0].date);
      const daysDiff = differenceInDays(incidentDate, deploymentDate);
      
      if (daysDiff >= 0 && daysDiff <= 7 && daysDiff < shortestDaysDiff) {
        shortestDaysDiff = daysDiff;
        linkedDeploymentDate = dateKey;
      }
    });
    
    // Only link the incident to the most recent deployment
    if (linkedDeploymentDate) {
      const dayData = apiData.get(linkedDeploymentDate);
      dayData.incidentDetails.push({
        incident_number: incidentNumber,
        incident_created_date: incidentDate,
        incident_description: incidentDesc,
        priority: incident.priority,
        status: incident.status || incident.state
      });
      dayData.postDeploymentIncidents++;
    }
  });

  // Ensure all dates in range have entries
  let currentDate = start;
  while (currentDate <= end) {
    const dateKey = format(currentDate, 'MMM d, yyyy');
    
    deploymentTimelineData.forEach((apiData) => {
      if (!apiData.has(dateKey)) {
        apiData.set(dateKey, {
          date: dateKey,
          deployments: 0,
          deploymentDetails: [],
          incidentDetails: [],
          postDeploymentIncidents: 0
        });
      }
    });
    
    currentDate = addDays(currentDate, 1);
  }
  
  return deploymentTimelineData;
};

// Helper function to convert timeline data to list format
export const convertTimelineToList = (timelineData: Map<string, Map<string, any>>) => {
  const result: any[] = [];
  
  // First collect all deployments
  timelineData.forEach((apiData, apiName) => {
    apiData.forEach((dayData, dateKey) => {
      // Include all deployments, even if there are no incidents
      if (dayData.deploymentDetails && dayData.deploymentDetails.length > 0) {
        dayData.deploymentDetails.forEach((deployment: any) => {
          result.push({
            apiName,
            date: dateKey,
            deploymentDate: deployment.date,
            taskNumber: deployment.taskNumber,
            description: deployment.description,
            incidents: dayData.incidentDetails || [],
            incidentCount: dayData.incidentDetails?.length || 0
          });
        });
      }
    });
  });
  
  // Sort by date in descending order
  return result.sort((a, b) => new Date(b.deploymentDate).getTime() - new Date(a.deploymentDate).getTime());
};

export function isDeploymentTask(task: any): boolean {
  const deploymentKeywords = [
    'deploy',
    'deployment',
    'redeploy',
    'deployement', // common misspelling
    'release',     
    'rollout',
    'promote',
    'upgrade',
    'install',
    'migration',
    'configure',
    'update'
  ];
  
  const systemKeywords = [
    '-sys',
    '-prc',
    '-exp',
    'etl-',
    'rpa-',
    'qad-',
    'erp-'
  ];
  
  const desc = (task.short_description || task.description || "").toLowerCase();
  return deploymentKeywords.some(kw => desc.includes(kw)) ||
         systemKeywords.some(kw => desc.includes(kw.toLowerCase()));
} 